import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import {
  getAllPaymentTransaction,
  getFirmNameDdl,
  getPaymentTransaction,
  IPaymentTransaction,
  IPaymentTransactionSearch,
  PAYMENT_TRANSACTION_SEARCH_INITIAL_VALUE,
} from "./PaymentTransactionViewController";
import { IOption, IPagination } from "../../data/AppInterface";
import { filterIcon, infoIcon } from "../../assets/icons/SvgIconList";
import {
  ORDER_BY,
  PAYMENT_ENTITY_TYPE_OPTION,
  PAYMENT_ENTITY_TYPE_STATUS_OPTION,
} from "../../utils/AppConstants";
import {
  AdvanceSearch,
  ICButton,
  ICModal,
  ICTable,
} from "../../core-component/ui";
import {
  ICDatePicker,
  ICDropDown,
  ICTextInput,
} from "../../core-component/form";
import { getLocalDate } from "../../utils/AppFunctions";
import { useLocation, useNavigate } from "react-router-dom";

let timer: ReturnType<typeof setTimeout>;

const PaymentTransactionView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentId = location?.state?.id;
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const { pagination, setChangePagination } = UsePaginationHook();
  const [paymentTransactionList, setPaymentTransactionList] = useState<
    IPaymentTransaction[]
  >([]);
  const [paymentTransactionToView, setPaymentTransactionToView] =
    useState<IPaymentTransaction>();
  const [isShowModal, setIsShowModal] = useState(false);
  const [advanceSearch, setAdvanceSearch] = useState<IPaymentTransactionSearch>(
    PAYMENT_TRANSACTION_SEARCH_INITIAL_VALUE,
  );
  const [firmName, setFirmName] = useState<IOption<string>[]>([]);
  useEffect(() => {
    fetchPaymentTransactionList({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);
  useEffect(() => {
    fetchFirmName();
  }, []);

  const fetchPaymentTransactionList = (paginationPlayLoad: IPagination) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      setIsLoading(true);
      await getAllPaymentTransaction(
        setPaymentTransactionList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };
  const fetchFirmName = async () => {
    await getFirmNameDdl(setFirmName);
  };
  const customActionView = (item: IPaymentTransaction) => {
    return (
      <div className="flex item-center justify-center w-full">
        {item.payment_failed_reason && (
          <div
            className="size-5 cursor-pointer"
            title={t("payment_failed_reason_tooltip")}
            onClick={() => {
              setIsShowModal(true);
              setPaymentTransactionToView(item);
            }}
          >
            {infoIcon}
          </div>
        )}
      </div>
    );
  };
  useEffect(() => {
    if (location.state) {
      setAdvanceSearch((prev) => ({
        ...prev,
        firmId: paymentId,
      }));
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);

  const filterView = (
    <div className="w-full grid lg:grid-cols-3 sm:grid-cols-1 gap-4 mt-3">
      <ICDropDown
        className="min-w-72"
        placeholder={t("firm_name")}
        selected={advanceSearch.firmId ? advanceSearch.firmId : undefined}
        options={firmName}
        searchable
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            firmId: option.data_value,
          }))
        }
      />
      <ICTextInput
        type="search"
        placeholder={t("entity_name")}
        onChange={(event) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            entityName: event.target.value,
          }))
        }
        value={advanceSearch.entityName}
        required
      />
      <ICDropDown
        className="min-w-72"
        placeholder={t("entity_type")}
        selected={advanceSearch.entityType}
        options={PAYMENT_ENTITY_TYPE_OPTION}
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            entityType: option.data_value,
          }))
        }
        searchable
      />
      <ICDatePicker
        useValue
        label={t("from_date")}
        placeholder={t("from_date")}
        value={
          advanceSearch.fromDate !== ""
            ? getLocalDate(advanceSearch.fromDate)
            : undefined
        }
        onChangeDate={(event) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            fromDate: event.target.value,
          }))
        }
      />
      <ICDatePicker
        useValue
        label={t("to_date")}
        min={getLocalDate(advanceSearch.fromDate)}
        placeholder={t("to_date")}
        value={
          advanceSearch.toDate !== ""
            ? getLocalDate(advanceSearch.toDate)
            : undefined
        }
        onChangeDate={(event) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            toDate: event.target.value,
          }))
        }
      />
      <ICTextInput
        type="search"
        placeholder={t("pg_order")}
        onChange={(event) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            pgOrder: event.target.value,
          }))
        }
        value={advanceSearch.pgOrder}
        required
      />
      <ICTextInput
        type="search"
        placeholder={t("invoice_number")}
        onChange={(event) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            invoiceNumber: event.target.value,
          }))
        }
        value={advanceSearch.invoiceNumber}
        required
      />
      <ICDropDown
        className="min-w-72"
        placeholder={t("payment_transaction_status")}
        selected={advanceSearch.paymentTransactionStatus}
        options={PAYMENT_ENTITY_TYPE_STATUS_OPTION}
        onSelect={(option) =>
          setAdvanceSearch((prev) => ({
            ...prev,
            paymentTransactionStatus: option.data_value,
          }))
        }
        searchable
      />

      <div className="!py-0 ml-4">
        <ICButton
          onClick={() =>
            setAdvanceSearch(PAYMENT_TRANSACTION_SEARCH_INITIAL_VALUE)
          }
          className="flex !w-fit"
        >
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );
  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        defaultOpen={!!paymentId}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchPaymentTransactionList(pagination);
        }}
        title={t("payment_transaction")}
      />

      <ICTable<IPaymentTransaction>
        isLoading={isLoading}
        totalFlex="grid grid-cols-18"
        fields={getPaymentTransaction(customActionView, t)}
        data={paymentTransactionList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchPaymentTransactionList({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchPaymentTransactionList({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchPaymentTransactionList({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />
      {isShowModal && paymentTransactionToView && (
        <ICModal
          title={t("payment_failed_reason_tooltip")}
          onClose={() => setIsShowModal(false)}
          children={paymentTransactionToView.payment_failed_reason}
        />
      )}
    </div>
  );
};
export default PaymentTransactionView;
